import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../lib/apiClient";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Table, TBody, TD, TH, THead, TR } from "../components/ui/Table";
import { Spinner } from "../components/ui/Spinner";
import { Modal } from "../components/ui/Modal";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { AgentForm, AgentFormValues } from "../components/agents/AgentForm";
import { toast } from "sonner";
import { useAuthStore } from "../store/auth";

type Agent = {
  id: string;
  name: string;
  email: string;
  role: "owner" | "admin" | "manager" | "agent" | "viewer";
  active: boolean;
  createdAt: string;
};

type AgentsResponse = {
  agents: Agent[];
};

export const AgentsPage = () => {
  const [createOpen, setCreateOpen] = useState(false);
  const [editState, setEditState] = useState<{ open: boolean; agent: Agent | null }>({
    open: false,
    agent: null
  });
  const [deleteTarget, setDeleteTarget] = useState<Agent | null>(null);
  const currentAgent = useAuthStore((state) => state.agent);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["agents"],
    queryFn: async () => {
      const response = await apiClient.get<AgentsResponse>("/agents");
      return response.data.agents;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (values: AgentFormValues) => {
      await apiClient.post("/agents", {
        name: values.name,
        email: values.email,
        role: values.role,
        password: values.password ?? ""
      });
    },
    onSuccess: () => {
      toast.success("Agent created");
      setCreateOpen(false);
      queryClient.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Failed to create agent")
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: AgentFormValues) => {
      if (!payload.id) throw new Error("Missing agent id");
      await apiClient.put(`/agents/${payload.id}`, {
        name: payload.name,
        email: payload.email,
        role: payload.role,
        password: payload.password ? payload.password : undefined
      });
    },
    onSuccess: () => {
      toast.success("Agent updated");
      setEditState({ open: false, agent: null });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Update failed")
  });

  const deleteMutation = useMutation({
    mutationFn: async (agentId: string) => {
      await apiClient.delete(`/agents/${agentId}`);
    },
    onSuccess: () => {
      toast.success("Agent deleted");
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Deletion failed")
  });

  const agents = useMemo(() => data ?? [], [data]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-100">Agent Directory</h2>
          <p className="text-sm text-slate-400">
            Provision, update, and retire multi-tenant support agents. All actions are validated and
            audited.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>Create Agent</Button>
      </div>

      <Card>
        <CardHeader>
          <p className="text-sm font-medium text-slate-200">Active directory</p>
          <p className="text-xs text-slate-500">
            {agents.length} agents | RBAC enforced | Cloudflare D1 backed
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : agents.length === 0 ? (
            <p className="text-sm text-slate-400">
              No agents yet. Invite your first teammate to start collaborating.
            </p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Email</TH>
                  <TH>Role</TH>
                  <TH>Status</TH>
                  <TH>Created</TH>
                  <TH></TH>
                </TR>
              </THead>
              <TBody>
                {agents.map((agent) => (
                  <TR key={agent.id}>
                    <TD className="font-medium text-slate-100">{agent.name}</TD>
                    <TD className="text-slate-400">{agent.email}</TD>
                    <TD>
                      <Badge variant="info" className="capitalize">
                        {agent.role}
                      </Badge>
                    </TD>
                    <TD>
                      <Badge variant={agent.active ? "success" : "warning"}>
                        {agent.active ? "Active" : "Disabled"}
                      </Badge>
                    </TD>
                    <TD className="text-xs text-slate-500">
                      {new Date(agent.createdAt).toLocaleString()}
                    </TD>
                    <TD>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditState({ open: true, agent })}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-200"
                          disabled={currentAgent?.id === agent.id}
                          onClick={() => setDeleteTarget(agent)}
                        >
                          Remove
                        </Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Modal
        title="Create Agent"
        description="Configure secure access for a new support agent."
        open={createOpen}
        onOpenChange={setCreateOpen}
      >
        <AgentForm
          mode="create"
          onSubmit={(values) => createMutation.mutateAsync(values)}
          defaultValues={{
            name: "",
            email: "",
            role: "agent",
            password: "",
            confirmPassword: ""
          }}
        />
      </Modal>

      <Modal
        title="Update Agent"
        description="Adjust RBAC permissions, rotate credentials, or update contact details."
        open={editState.open}
        onOpenChange={(value) => setEditState((prev) => ({ ...prev, open: value }))}
      >
        {editState.agent ? (
          <AgentForm
            mode="update"
            defaultValues={{
              id: editState.agent.id,
              name: editState.agent.name,
              email: editState.agent.email,
              role: editState.agent.role,
              password: "",
              confirmPassword: ""
            }}
            onSubmit={(values) =>
              updateMutation.mutateAsync({
                ...values,
                id: editState.agent?.id
              })
            }
          />
        ) : null}
      </Modal>

      <ConfirmDialog
        title="Remove agent"
        description={
          deleteTarget ? (
            <span>
              Are you sure you want to remove <strong>{deleteTarget.name}</strong> (
              {deleteTarget.email})? This revokes access immediately.
            </span>
          ) : (
            "This action cannot be undone."
          )
        }
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        confirmLabel="Remove agent"
        loadingLabel="Removing..."
        destructive
        loading={deleteMutation.isPending}
        onConfirm={async () => {
          if (!deleteTarget) return;
          try {
            await deleteMutation.mutateAsync(deleteTarget.id);
          } catch {
            // handled via mutation onError
          }
        }}
      />
    </div>
  );
};
